import type { Phrase } from './copy.js';

/**
 * The balkonahe (Pusoy) and the barangay outpost (Gera).
 *
 * Two boards, one fragment, because they are the same area of the app: a hand
 * you arrange in private and then push across the felt. Where a Filipino table
 * term has a real English one it is used — bunot/draw, sapaw/top, tapon/discard,
 * mesa/table — and the piece grades are generic military ones in both languages
 * (heneral/general, tiktik/spy), which is what the venue's IP guardrails ask for.
 *
 * "Misset", "scoop", "royalty" and the poker hand classes are terms of art that
 * Filipino tables already say in English, so they read the same in both.
 */
export const PUSOY = {
  /* ── Pusoy · the felt ─────────────────────────────────────────────────── */
  'pusoy.wait': {
    tl: 'Naghihintay ng baraha mula sa server…',
    en: 'Waiting for cards from the server…',
  },
  'pusoy.row.back': { tl: 'Likod', en: 'Back' },
  'pusoy.row.middle': { tl: 'Gitna', en: 'Middle' },
  'pusoy.row.front': { tl: 'Harap', en: 'Front' },
  'pusoy.row.short': { tl: 'kulang pa', en: 'not full' },
  'pusoy.row.empty': { tl: 'Bakanteng puwesto sa {row}', en: 'Empty spot in {row}' },

  /* ── Pusoy · the near rail ────────────────────────────────────────────── */
  'pusoy.rail.watching': {
    tl: 'Nanonood ka — walang baraha sa rail.',
    en: 'You’re watching — no cards on the rail.',
  },
  'pusoy.rail.locked': { tl: 'Naka-lock in na ang board mo.', en: 'Your board is locked in.' },
  'pusoy.rail.done': {
    tl: 'Nakalatag lahat. Tingnan mo ulit bago mag-lock in.',
    en: 'All laid out. Look it over before you lock in.',
  },

  /* ── Pusoy · naturals and the misset warning ──────────────────────────── */
  'pusoy.natural.auto': {
    tl: 'Awtomatiko ito — walang misset, kahit anong ayos.',
    en: 'It pays automatically — no misset, whatever the layout.',
  },
  'pusoy.natural.unknown': { tl: 'natural', en: 'natural' },
  'pusoy.misset': { tl: 'Misset.', en: 'Misset.' },
  'pusoy.misset.back': {
    tl: 'Mas malakas ang gitna kaysa likod.',
    en: 'The middle is stronger than the back.',
  },
  'pusoy.misset.middle': {
    tl: 'Mas malakas ang harap kaysa gitna.',
    en: 'The front is stronger than the middle.',
  },
  'pusoy.misset.cost': {
    tl: 'Kung ganito ang i-lock in mo, talo ka sa lahat ng hanay.',
    en: 'Lock this in and you lose every row.',
  },

  /* ── Pusoy · hints ────────────────────────────────────────────────────── */
  'pusoy.ready': {
    tl: 'Handa na ang board mo — hinihintay lang ang oras mo.',
    en: 'Your board is ready — just waiting on your turn.',
  },
  'pusoy.hint.holding': {
    tl: 'May hawak ka — pindutin ang hanay o ang ipapalit dito.',
    en: 'You’re holding one — tap a row, or the card to swap it with.',
  },
  'pusoy.hint.left': {
    tl: '{n} baraha pa. Pindutin ang baraha, tapos ang hanay.',
    en: '{n} cards to go. Tap a card, then a row.',
  },
  'pusoy.hint.ok': {
    tl: 'Ayos ang hanay: likod ≥ gitna ≥ harap.',
    en: 'Rows are in order: back ≥ middle ≥ front.',
  },

  /* ── Pusoy · whose clock ──────────────────────────────────────────────── */
  'pusoy.turn.reveal': { tl: 'Nakabukas na ang lahat ng board.', en: 'Every board is face up.' },
  'pusoy.turn.wait': { tl: 'Naghihintay…', en: 'Waiting…' },
  'pusoy.turn.arranging': { tl: 'Nag-aayos si {who}.', en: '{who} is arranging.' },
  'pusoy.turn.locked': { tl: 'Naka-lock in ka na.', en: 'You’re locked in.' },
  'pusoy.turn.you': { tl: 'Ikaw na — ayusin mo na.', en: 'You’re up — arrange your board.' },
  'pusoy.turn.lockedwait': {
    tl: 'Naka-lock in ka na. Hinihintay si {who}.',
    en: 'You’re locked in. Waiting on {who}.',
  },
  'pusoy.turn.theirs': {
    tl: 'Kay {who} ang oras. Pwede ka nang mag-ayos.',
    en: '{who} is on the clock. You can arrange now.',
  },

  /* ── Pusoy · buttons ──────────────────────────────────────────────────── */
  'pusoy.act.lock': { tl: 'Lock in', en: 'Lock in' },
  'pusoy.act.lockAnyway': { tl: 'Lock in pa rin', en: 'Lock in anyway' },
  'pusoy.act.auto': { tl: 'Auto-ayos', en: 'Auto-sort' },

  /* ── Pusoy · the seat pods ────────────────────────────────────────────── */
  'pusoy.seat.you': { tl: 'Ikaw', en: 'You' },
  'pusoy.seat.n': { tl: 'Upuan {n}', en: 'Seat {n}' },
  'pusoy.seat.quit': { tl: 'alis', en: 'left' },
  'pusoy.seat.clock': { tl: 'oras', en: 'clock' },
  'pusoy.seat.locked': { tl: 'lock in', en: 'locked' },

  /* ── Pusoy · the reveal ───────────────────────────────────────────────── */
  'pusoy.reveal.misset': { tl: 'misset', en: 'misset' },
  'pusoy.reveal.royalty': { tl: '+{n} royalty', en: '+{n} royalty' },

  /* ── Pusoy · the verdict ──────────────────────────────────────────────── */
  'pusoy.over.counting': { tl: 'Binibilang pa…', en: 'Still counting…' },
  'pusoy.over.tabla': {
    tl: 'Tabla — walang kumuha ng pot, balik lahat ng buy-in.',
    en: 'Draw — nobody takes the pot, every buy-in comes back.',
  },
  'pusoy.over.forfeit': { tl: 'May umalis — void ang hand.', en: 'Someone left — the hand is void.' },
  'pusoy.over.none': { tl: 'Walang panalo sa hand na ’to.', en: 'Nobody won this hand.' },
  'pusoy.over.you': {
    tl: 'Ikaw ang pinakamataas. Sa ’yo ang pot.',
    en: 'You came out highest. The pot is yours.',
  },
  'pusoy.over.who': { tl: 'Kay {who} ang pot.', en: 'The pot goes to {who}.' },
  'pusoy.scoop.you': { tl: 'Scoop! Nilinis mo si {who}.', en: 'Scoop! You swept {who}.' },
  'pusoy.scoop.onyou': { tl: 'Nilinis ka ni {who}.', en: '{who} swept you.' },
  'pusoy.scoop.other': { tl: 'Scoop ni {winner} kay {loser}.', en: '{winner} scooped {loser}.' },

  /* ── Pusoy · hand classes ─────────────────────────────────────────────── */
  'pusoy.cat.highCard': { tl: 'mataas na baraha', en: 'high card' },
  'pusoy.cat.pair': { tl: 'pares', en: 'pair' },
  'pusoy.cat.twoPair': { tl: 'dalawang pares', en: 'two pair' },
  'pusoy.cat.trips': { tl: 'tatluhan', en: 'trips' },
  'pusoy.cat.straight': { tl: 'straight', en: 'straight' },
  'pusoy.cat.flush': { tl: 'flush', en: 'flush' },
  'pusoy.cat.fullHouse': { tl: 'full house', en: 'full house' },
  'pusoy.cat.quads': { tl: 'apat na magkapareho', en: 'quads' },
  'pusoy.cat.straightFlush': { tl: 'straight flush', en: 'straight flush' },

  /* ── Pusoy · a card, read aloud ───────────────────────────────────────── */
  'pusoy.card': { tl: '{rank} ng {suit}', en: '{rank} of {suit}' },
  'pusoy.suit.S': { tl: 'espada', en: 'spades' },
  'pusoy.suit.H': { tl: 'puso', en: 'hearts' },
  'pusoy.suit.D': { tl: 'diamante', en: 'diamonds' },
  'pusoy.suit.C': { tl: 'trebol', en: 'clubs' },

  /* ── Gera · the desk ──────────────────────────────────────────────────── */
  'pusoy.gera.wait': {
    tl: 'Naghihintay ng mesa mula sa server…',
    en: 'Waiting for a table from the server…',
  },
  'pusoy.gera.desk': { tl: 'Mesa ng outpost', en: 'Outpost table' },
  'pusoy.gera.sq': { tl: '{sq} — {what}', en: '{sq} — {what}' },
  'pusoy.gera.piece.seat': { tl: 'piyesa ng Upuan {n}', en: 'Seat {n}’s piece' },
  'pusoy.gera.piece.mine': { tl: 'piyesa mo', en: 'your piece' },
  'pusoy.gera.piece.foe': { tl: 'piyesa ng kalaban', en: 'enemy piece' },
  'pusoy.gera.tray': { tl: '{rank} — {n} pa', en: '{rank} — {n} left' },

  /* ── Gera · rank names and the codes on the standees ──────────────────── */
  'pusoy.gera.rank.heneral': { tl: 'Heneral', en: 'General' },
  'pusoy.gera.rank.opisyal': { tl: 'Opisyal', en: 'Officer' },
  'pusoy.gera.rank.tanod': { tl: 'Tanod', en: 'Guard' },
  'pusoy.gera.rank.tiktik': { tl: 'Tiktik', en: 'Spy' },
  'pusoy.gera.rank.watawat': { tl: 'Watawat', en: 'Flag' },
  'pusoy.gera.code.heneral': { tl: 'H', en: 'G' },
  'pusoy.gera.code.opisyal': { tl: 'O', en: 'O' },
  'pusoy.gera.code.tanod': { tl: 'TD', en: 'GD' },
  'pusoy.gera.code.tiktik': { tl: 'TK', en: 'SP' },
  'pusoy.gera.code.watawat': { tl: 'WW', en: 'FL' },
  'pusoy.gera.rank.graded': { tl: '{name} {grade}', en: '{name} {grade}' },
  'pusoy.gera.code.graded': { tl: '{code}{grade}', en: '{code}{grade}' },

  /* ── Gera · the flag run ──────────────────────────────────────────────── */
  'pusoy.gera.flag.title': { tl: 'Nasa dulo na ang watawat', en: 'The flag is at the far end' },
  'pusoy.gera.flag.watch': {
    tl: ' — kay Upuan {n}, at isang tira na lang ang sagot.',
    en: ' — it’s Seat {n}’s, and there is one move left to answer it.',
  },
  'pusoy.gera.flag.yours': {
    tl: ' — isang tira pa ng kalaban; kung buhay pa ito, panalo ka.',
    en: ' — one more move from your opponent; if it lives, you win.',
  },
  'pusoy.gera.flag.theirs': {
    tl: ' — ngayon lang ang tsansa mong kunin.',
    en: ' — this is your only chance to take it.',
  },

  /* ── Gera · setting up ────────────────────────────────────────────────── */
  'pusoy.gera.setup.ready': {
    tl: 'Handa na ang latag mo — hinihintay lang ang oras mo.',
    en: 'Your layout is ready — just waiting on your turn.',
  },
  'pusoy.gera.setup.count': {
    tl: 'nakalatag — pindutin ang piyesa, tapos ang parisukat.',
    en: 'placed — tap a piece, then a square.',
  },
  'pusoy.gera.setup.drop': {
    tl: 'Pindutin ang parisukat sa hanay mo.',
    en: 'Tap a square in your own zone.',
  },
  'pusoy.gera.setup.move': {
    tl: 'Pindutin ang bagong parisukat, o ang parehong parisukat para ibalik.',
    en: 'Tap a new square, or the same one to put it back.',
  },
  'pusoy.gera.picked': {
    tl: 'ang napili — piliin kung saan ililipat.',
    en: 'is up — pick where it goes.',
  },
  'pusoy.gera.piece': { tl: 'Piyesa', en: 'Piece' },

  /* ── Gera · buttons ───────────────────────────────────────────────────── */
  'pusoy.gera.act.deploy': { tl: 'Ilatag na', en: 'Deploy' },
  'pusoy.gera.act.clear': { tl: 'Alisin lahat', en: 'Clear all' },
  'pusoy.gera.act.reveal': { tl: 'Ipakita ang hukbo', en: 'Show the armies' },
  'pusoy.gera.act.back': { tl: 'Bawi', en: 'Back' },
  'pusoy.gera.act.resign': { tl: 'Sumuko', en: 'Resign' },
  'pusoy.gera.act.resignSure': { tl: 'Sigurado — sumuko', en: 'Sure — resign' },

  /* ── Gera · the three presets ─────────────────────────────────────────── */
  'pusoy.gera.presets.title': { tl: 'Tatlong latag', en: 'Three layouts' },
  'pusoy.gera.presets.note': {
    tl: 'Isang pindot, buong 21 — tapos ilipat-lipat kung saan mo gusto.',
    en: 'One tap, all 21 — then move them wherever you like.',
  },
  'pusoy.gera.preset.n': { tl: 'Latag {n}', en: 'Layout {n}' },
  'pusoy.gera.form.harang': { tl: 'Harang', en: 'Block' },
  'pusoy.gera.form.harang.note': {
    tl: 'Watawat sa likod, tiktik sa magkabilang gilid.',
    en: 'Flag at the back, spies on both flanks.',
  },
  'pusoy.gera.form.kuta': { tl: 'Kuta', en: 'Fort' },
  'pusoy.gera.form.kuta.note': {
    tl: 'Watawat sa sulok, pader na tanod sa harap.',
    en: 'Flag in the corner, a wall of guards in front.',
  },
  'pusoy.gera.form.sugod': { tl: 'Sugod', en: 'Charge' },
  'pusoy.gera.form.sugod.note': {
    tl: 'Heneral sa unahan, magaan ang bantay sa watawat.',
    en: 'Generals up front, a light guard on the flag.',
  },

  /* ── Gera · the army card ─────────────────────────────────────────────── */
  'pusoy.gera.army': { tl: 'Hukbo', en: 'Army' },
  'pusoy.gera.army.mine': { tl: 'Hukbo mo', en: 'Your army' },
  'pusoy.gera.army.foe': { tl: 'Kalaban', en: 'Opponent' },
  'pusoy.gera.tally': {
    tl: '{n} sagupaan · {m} tira walang bakbakan',
    en: '{n} clashes · {m} moves with no fight',
  },
  'pusoy.gera.tally.repeat': { tl: ' · {n}× ulit', en: ' · {n}× repeat' },
  'pusoy.gera.ladder': {
    tl: 'Ranggo at ang tatlong palusot',
    en: 'Ranks and the three exceptions',
  },
  'pusoy.gera.ladder.note': {
    tl: 'Talo ng tiktik lahat ng opisyal at heneral — pero patay ang tiktik sa tanod. Talo ang watawat sa lahat; pero kapag ito ang sumugod sa watawat ng kalaban, ito ang panalo.',
    en: 'The spy takes every officer and general — but a guard kills the spy. The flag loses to everything; charge it into their flag, though, and it wins.',
  },
  'pusoy.gera.reveal.done': { tl: 'Bukas na ang dalawang hukbo.', en: 'Both armies are face up.' },
  'pusoy.gera.reveal.waiting': {
    tl: 'Pumayag ka na. Hinihintay ang kalaban.',
    en: 'You’ve agreed. Waiting on your opponent.',
  },
  'pusoy.gera.reveal.offer': {
    tl: 'Kapag pumayag kayong dalawa, buburahin ang lahat ng ranggo sa mesa. Hindi mababawi.',
    en: 'If you both agree, every rank on the table is uncovered. No taking it back.',
  },

  /* ── Gera · whose move ────────────────────────────────────────────────── */
  'pusoy.gera.over': { tl: 'Tapos na ang laban.', en: 'The match is over.' },
  'pusoy.gera.turn.deployAll': {
    tl: 'Naglalatag pa sila ng hukbo.',
    en: 'They’re still laying out their armies.',
  },
  'pusoy.gera.turn.deployYou': { tl: 'Ilatag mo ang 21 mo.', en: 'Lay out your 21.' },
  'pusoy.gera.turn.deployFoe': {
    tl: 'Naglalatag pa ang kalaban.',
    en: 'Your opponent is still laying out.',
  },
  'pusoy.gera.turn.wait': { tl: 'Naghihintay…', en: 'Waiting…' },
  'pusoy.gera.turn.seat': { tl: 'Tira ng Upuan {n}.', en: 'Seat {n} to move.' },
  'pusoy.gera.turn.you': { tl: 'Ikaw ang tira.', en: 'Your move.' },
  'pusoy.gera.turn.foe': { tl: 'Kalaban ang tira.', en: 'Opponent to move.' },

  /* ── Gera · how it ended ──────────────────────────────────────────────── */
  'pusoy.gera.why.flagCaptured': { tl: 'Nakuha ang watawat.', en: 'The flag was taken.' },
  'pusoy.gera.why.flagHome': {
    tl: 'Nakarating ang watawat sa kabilang dulo.',
    en: 'The flag reached the far end.',
  },
  'pusoy.gera.why.noMoves': { tl: 'Walang matirang tira.', en: 'No moves left.' },
  'pusoy.gera.why.resign': { tl: 'Sumuko.', en: 'Resigned.' },
  'pusoy.gera.why.draw': { tl: 'Tabla.', en: 'Draw.' },
  'pusoy.gera.why.forfeit': { tl: 'May umalis sa mesa.', en: 'Someone left the table.' },
  'pusoy.gera.draw.repetition': {
    tl: ' Paulit-ulit ang posisyon.',
    en: ' The position kept repeating.',
  },
  'pusoy.gera.draw.noProgress': {
    tl: ' Matagal nang walang bakbakan.',
    en: ' Nothing has happened in a long while.',
  },
  'pusoy.gera.over.draw': {
    tl: '{why}{how} Walang kumuha ng pot.',
    en: '{why}{how} Nobody takes the pot.',
  },
  'pusoy.gera.over.seat': { tl: '{why} Panalo ang Upuan {n}.', en: '{why} Seat {n} wins.' },
  'pusoy.gera.over.you': { tl: '{why} Panalo ka.', en: '{why} You win.' },
  'pusoy.gera.over.foe': { tl: '{why} Panalo ang kalaban.', en: '{why} Your opponent wins.' },
  /* Singulars: English inflects after a count and Taglish does not. */
  'pusoy.hint.left.one': { tl: '{n} baraha pa. Pindutin ang baraha, tapos ang hanay.', en: '{n} card to go. Tap a card, then a row.' },
  'pusoy.gera.tally.one': { tl: '{n} sagupaan · {m} tira walang bakbakan', en: '{n} clash · {m} moves with no fight' },
  'pusoy.rows.label': { tl: 'Mga hanay', en: 'Your rows' },
} as const satisfies Record<string, Phrase>;
